import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ArrowLeft, Clock, Calendar, Tag, Phone } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import JsonLd from '../components/JsonLd';
import { BlogIconBanner, BlogIconCard } from '../components/BlogIconBanner';
import { blogArticles } from '../data/blogArticles';
import { phoneNumber, whatsappLink, cities } from '../data/mock';
import { Button } from '../components/ui/button';

const BlogArticlePage = () => {
  const { articleSlug } = useParams();
  const article = blogArticles.find(a => a.slug === articleSlug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [articleSlug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-amber-400 mb-4">Article non trouvé</h1>
          <Link to="/blog" className="text-amber-300 hover:text-amber-400 underline">
            Retour au blog
          </Link>
        </div>
      </div>
    );
  }

  const relatedCities = article.relatedCities
    .map(slug => cities.find(c => c.slug === slug))
    .filter(Boolean);

  const otherArticles = blogArticles.filter(a => a.id !== article.id).slice(0, 3);

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>{article.metaTitle}</title>
        <meta name="description" content={article.metaDescription} />
        <link rel="canonical" href={`https://maraboutserieux.com/blog/${article.slug}`} />
        <link rel="alternate" hrefLang="fr" href={`https://maraboutserieux.com/blog/${article.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.metaTitle} />
        <meta property="og:description" content={article.metaDescription} />
        <meta property="og:url" content={`https://maraboutserieux.com/blog/${article.slug}`} />
        <meta property="og:locale" content="fr_FR" />
        <meta property="article:published_time" content={article.publishDate} />
        <meta property="article:modified_time" content={article.modifiedDate} />
      </Helmet>

      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": article.title,
        "description": article.metaDescription,
        "datePublished": article.publishDate,
        "dateModified": article.modifiedDate,
        "author": {
          "@type": "Organization",
          "name": "Marabout Sérieux",
          "url": "https://maraboutserieux.com"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Marabout Sérieux",
          "url": "https://maraboutserieux.com"
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://maraboutserieux.com/blog/${article.slug}`
        }
      }} />

      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://maraboutserieux.com/" },
          { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://maraboutserieux.com/blog" },
          { "@type": "ListItem", "position": 3, "name": article.title, "item": `https://maraboutserieux.com/blog/${article.slug}` }
        ]
      }} />

      <Header />

      <article className="pt-28 pb-10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link
              to="/blog"
              className="inline-flex items-center text-amber-400 hover:text-amber-300 mb-6 transition-colors"
              data-testid="blog-back-link"
            >
              <ArrowLeft size={18} className="mr-2" />
              Retour au blog
            </Link>

            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="flex items-center gap-1 text-xs px-3 py-1 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-300">
                  <Tag size={12} />
                  {article.category}
                </span>
                <span className="flex items-center gap-1 text-xs text-amber-100/50">
                  <Clock size={12} />
                  {article.readTime} de lecture
                </span>
                <span className="flex items-center gap-1 text-xs text-amber-100/50">
                  <Calendar size={12} />
                  {new Date(article.publishDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              </div>

              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-amber-200 via-amber-400 to-amber-200 bg-clip-text text-transparent leading-tight">
                {article.title}
              </h1>
            </div>

            <BlogIconBanner icon={article.icon} size="large" />

            <div className="prose-custom space-y-5">
              {article.content.map((block, index) => {
                if (block.type === 'heading') {
                  return (
                    <h2 key={`h-${index}`} className="text-xl md:text-2xl font-bold text-amber-400 mt-8 mb-3">
                      {block.text}
                    </h2>
                  );
                }
                if (block.type === 'paragraph') {
                  return (
                    <p key={`p-${index}`} className="text-amber-100/80 leading-relaxed text-sm md:text-base">
                      {block.text}
                    </p>
                  );
                }
                if (block.type === 'list') {
                  return (
                    <ul key={`l-${index}`} className="space-y-2 ml-4">
                      {block.items.map((item) => (
                        <li key={item.slice(0, 30)} className="flex items-start gap-2 text-amber-100/80 text-sm md:text-base">
                          <span className="text-amber-400 mt-1 flex-shrink-0">&#9679;</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  );
                }
                return null;
              })}
            </div>

            {/* CTA Section */}
            <div className="mt-10 bg-gradient-to-br from-zinc-900/90 to-black border border-amber-600/40 rounded-2xl p-6 md:p-8 text-center">
              <h3 className="text-xl md:text-2xl font-bold text-amber-400 mb-3">
                Besoin d'aide ? Contactez Le Grand Marabout
              </h3>
              <p className="text-amber-100/70 mb-6 text-sm md:text-base">
                Consultation gratuite 24h/24 et 7j/7. Résultats garantis sous 7 jours.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href={`tel:${phoneNumber}`}>
                  <Button className="w-full sm:w-auto bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-8 py-5 text-base shadow-lg shadow-red-500/50 transform hover:scale-105 transition-all duration-300">
                    <Phone className="mr-2" size={20} />
                    Appeler Maintenant
                  </Button>
                </a>
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                  <Button className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white px-8 py-5 text-base shadow-lg shadow-green-500/50 transform hover:scale-105 transition-all duration-300">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    WhatsApp Direct
                  </Button>
                </a>
              </div>
            </div>

            {/* Related Cities - Internal Linking */}
            {relatedCities.length > 0 && (
              <div className="mt-10">
                <h3 className="text-lg font-bold text-amber-400 mb-4">
                  Le Grand Marabout intervient dans ces villes
                </h3>
                <div className="flex flex-wrap gap-3">
                  {relatedCities.map(city => (
                    <Link
                      key={city.id}
                      to={`/${city.slug}`}
                      className="px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-200 text-sm hover:bg-amber-500/20 hover:text-amber-100 transition-all"
                    >
                      Marabout {city.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Other Articles */}
            <div className="mt-12">
              <h3 className="text-xl font-bold text-amber-400 mb-6">
                Articles Similaires
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {otherArticles.map(a => (
                  <Link key={a.id} to={`/blog/${a.slug}`} className="group">
                    <div className="bg-gradient-to-br from-zinc-900 to-black border border-amber-600/30 rounded-xl overflow-hidden hover:border-amber-500/50 transition-all">
                      <BlogIconCard icon={a.icon} />
                      <div className="p-4">
                        <h4 className="text-sm font-bold text-amber-400 group-hover:text-amber-300 line-clamp-2">
                          {a.title}
                        </h4>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogArticlePage;
